import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  Trophy, Check, CalendarBlank, Warning, ArrowCircleDown, Lightning,
  Receipt, DownloadSimple,
} from "@phosphor-icons/react";
import api from "@/lib/api";

const TIER_ORDER = ["starter", "growth", "pro", "enterprise"];
const TIER_ACCENT = {
  starter: "text-text-secondary",
  growth: "text-lime-300",
  pro: "text-brand-accent",
  enterprise: "text-heat",
};

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return null;
  }
};

export const AcademyPlanBilling = ({ academyId, onChanged, autoRenewTier, autoRenewCycle, onAutoRenewHandled }) => {
  const [info, setInfo] = useState(null);
  const [cycle, setCycle] = useState(autoRenewCycle === "yearly" ? "yearly" : "monthly");
  const [busy, setBusy] = useState(null);
  const [history, setHistory] = useState([]);
  const autoFired = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/academies/${academyId}/plan/packages`);
      setInfo(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load plans");
    }
    try {
      const { data } = await api.get(`/academies/${academyId}/plan/history`);
      setHistory(data.payments || []);
    } catch {
      /* history is non-critical */
    }
  }, [academyId]);

  useEffect(() => { load(); }, [load]);

  const pkgFor = (tier) =>
    (info?.packages || []).find((p) => p.id === `academy_${tier}_${cycle}`);

  const checkout = async (tier) => {
    const pkg = pkgFor(tier);
    if (!pkg) return;
    if (!info?.razorpay_enabled || typeof window.Razorpay !== "function") {
      toast.error("Payments are not available right now — please try again later");
      return;
    }
    setBusy(tier);
    try {
      const { data: order } = await api.post(`/academies/${academyId}/plan/order`, { package_id: pkg.id });
      const options = {
        key: order.key_id,
        amount: order.amount_paise,
        currency: order.currency,
        name: "Edge Training",
        description: order.label,
        order_id: order.order_id,
        prefill: { name: order.user_name || "", email: order.user_email || "" },
        theme: { color: "#1E3A8A" },
        handler: async (resp) => {
          try {
            const { data } = await api.post(`/academies/${academyId}/plan/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              package_id: pkg.id,
            });
            toast.success(`Upgraded to ${data.plan?.toUpperCase?.() || "new plan"} — seats unlocked`, { duration: 5000 });
            await load();
            onChanged?.();
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment verification failed");
          } finally {
            setBusy(null);
          }
        },
        modal: { ondismiss: () => { setBusy(null); toast.info("Checkout cancelled"); } },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (r) => { toast.error(r?.error?.description || "Payment failed"); setBusy(null); });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(null);
    }
  };

  // One-tap renewal from the reminder email / notification deep link:
  // auto-open the checkout for the requested tier once plans are loaded.
  const paidTierSet = new Set(["growth", "pro", "enterprise"]);
  useEffect(() => {
    if (autoFired.current) return;
    if (!autoRenewTier || !paidTierSet.has(autoRenewTier)) return;
    if (!info) return;
    autoFired.current = true;
    onAutoRenewHandled?.();
    if (!info.razorpay_enabled || typeof window.Razorpay !== "function") {
      toast.info("Choose your plan below to renew");
      return;
    }
    checkout(autoRenewTier);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info, autoRenewTier]);

  const downloadInvoice = async (invoiceNo) => {
    try {
      const res = await api.get(`/academies/${academyId}/plan/invoice/${invoiceNo}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `edge-training-invoice-${invoiceNo}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download invoice");
    }
  };

  const downgrade = async () => {
    if (!window.confirm("Cancel your paid plan and return to the free Starter tier? Existing members stay, but you won't be able to add beyond Starter limits.")) return;
    setBusy("downgrade");
    try {
      await api.patch(`/academies/${academyId}`, { plan: "starter" });
      toast.success("Switched to the free Starter plan");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change plan");
    } finally {
      setBusy(null);
    }
  };

  if (!info) {
    return <div className="label-tiny text-text-secondary" data-testid="academy-plan-billing-loading">Loading plans…</div>;
  }

  const current = info.current_plan || "starter";
  const renewal = fmtDate(info.plan_until);
  const paidTiers = ["growth", "pro", "enterprise"];

  return (
    <div className="space-y-5" data-testid="academy-plan-billing">
      {/* Current plan + renewal */}
      <div className="flex flex-wrap items-center justify-between gap-3 border border-white/10 bg-white/[0.02] px-4 py-3">
        <div className="flex items-center gap-2">
          <Trophy size={16} weight="fill" className={TIER_ACCENT[current]} />
          <span className="label-tiny text-[11px] text-text-secondary">CURRENT PLAN</span>
          <span className={`label-tiny text-[11px] ${TIER_ACCENT[current]}`} data-testid="academy-plan-current">
            {(info.tiers?.[current]?.label || current).toUpperCase()}
          </span>
          {renewal && (
            <span className="inline-flex items-center gap-1 label-tiny text-[11px] text-text-secondary ml-1">
              <CalendarBlank size={12} /> Renews {renewal}
            </span>
          )}
        </div>
        {current !== "starter" && (
          <button
            onClick={downgrade}
            disabled={busy === "downgrade"}
            className="inline-flex items-center gap-1 label-tiny text-[11px] text-text-secondary hover:text-heat transition-colors disabled:opacity-50"
            data-testid="academy-plan-downgrade"
          >
            <ArrowCircleDown size={13} /> CANCEL TO STARTER
          </button>
        )}
      </div>

      {info.placeholder_pricing && (
        <div className="flex items-start gap-2 border border-amber-400/40 bg-[#241a07] px-3 py-2 text-[11px] text-amber-50" data-testid="academy-plan-placeholder-warning">
          <Warning size={14} weight="fill" className="text-amber-400 mt-0.5 shrink-0" />
          <span>Prices shown are placeholders. Set your real tier pricing before launch.</span>
        </div>
      )}

      {/* Billing cycle toggle */}
      <div className="inline-flex border border-white/10 rounded-full p-0.5 bg-white/[0.02]" data-testid="academy-plan-cycle">
        {["monthly", "yearly"].map((c) => (
          <button
            key={c}
            onClick={() => setCycle(c)}
            className={`px-4 py-1.5 rounded-full label-tiny text-[11px] transition-colors ${cycle === c ? "bg-brand-accent text-surface-base" : "text-text-secondary hover:text-white"}`}
            data-testid={`academy-plan-cycle-${c}`}
          >
            {c === "yearly" ? "YEARLY · 2 MONTHS FREE" : "MONTHLY"}
          </button>
        ))}
      </div>

      {/* Tier cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {paidTiers.map((tier) => {
          const t = info.tiers?.[tier] || {};
          const pkg = pkgFor(tier);
          const isCurrent = current === tier;
          const coaches = t.coaches == null ? "Unlimited" : t.coaches;
          const athletes = t.athletes == null ? "Unlimited" : t.athletes;
          return (
            <div
              key={tier}
              className={`relative border p-5 flex flex-col gap-4 ${isCurrent ? "border-brand-accent bg-brand-accent/[0.06]" : "border-white/10 bg-white/[0.02]"}`}
              data-testid={`academy-plan-tier-${tier}`}
            >
              {tier === "pro" && !isCurrent && (
                <span className="absolute -top-2 right-4 bg-brand-accent text-surface-base label-tiny text-[9px] px-2 py-0.5">POPULAR</span>
              )}
              <div className="flex items-baseline justify-between">
                <span className={`font-heading text-xl ${TIER_ACCENT[tier]}`}>{t.label || tier}</span>
                {isCurrent && (
                  <span className="label-tiny text-[9px] text-brand-accent border border-brand-accent/50 px-2 py-0.5" data-testid={`academy-plan-badge-${tier}`}>CURRENT</span>
                )}
              </div>
              <div>
                <div className="text-3xl font-heading tabular-nums">{fmtINR(pkg?.amount_inr)}</div>
                <div className="label-tiny text-[10px] text-text-secondary mt-0.5">
                  per {cycle === "yearly" ? "year" : "month"}
                </div>
              </div>
              <ul className="space-y-1.5 text-sm text-text-secondary flex-1">
                <li className="flex items-center gap-2"><Check size={14} className="text-brand-accent shrink-0" /> {coaches} coaches</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-brand-accent shrink-0" /> {athletes} athletes</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-brand-accent shrink-0" /> {t.features}</li>
              </ul>
              <button
                onClick={() => checkout(tier)}
                disabled={busy === tier}
                className={`w-full py-2.5 label-tiny text-[11px] tracking-wider transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-1.5 ${isCurrent ? "border border-brand-accent text-brand-accent hover:bg-brand-accent/10" : "bg-brand-accent text-surface-base hover:bg-brand-accent/90"}`}
                data-testid={`academy-plan-upgrade-${tier}`}
              >
                <Lightning size={13} weight="fill" />
                {busy === tier ? "STARTING…" : isCurrent ? "RENEW" : `UPGRADE TO ${(t.label || tier).toUpperCase()}`}
              </button>
            </div>
          );
        })}
      </div>

      {/* Billing history */}
      {history.length > 0 && (
        <div className="border border-white/10 bg-white/[0.02]" data-testid="academy-plan-history">
          <div className="px-4 py-2.5 border-b border-white/10 flex items-center gap-2">
            <Receipt size={14} className="text-text-secondary" />
            <span className="label-tiny text-[11px] text-text-secondary">BILLING HISTORY</span>
          </div>
          <div className="divide-y divide-white/5">
            {history.map((p) => (
              <div
                key={p.invoice_no}
                className="flex flex-wrap items-center justify-between gap-2 px-4 py-3"
                data-testid={`academy-plan-history-row-${p.invoice_no}`}
              >
                <div className="min-w-0">
                  <div className="text-sm text-white font-medium">
                    {p.label}
                    <span className="ml-2 label-tiny text-[9px] text-text-secondary border border-white/10 px-1.5 py-0.5">
                      {p.billing === "yearly" ? "YEARLY" : "MONTHLY"}
                    </span>
                  </div>
                  <div className="label-tiny text-[10px] text-text-secondary mt-1">
                    {fmtDate(p.paid_at) || "—"} · {p.invoice_no}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm tabular-nums text-white">{fmtINR(p.amount_inr)}</span>
                  <button
                    onClick={() => downloadInvoice(p.invoice_no)}
                    className="inline-flex items-center gap-1 label-tiny text-[10px] text-brand-accent hover:text-white transition-colors"
                    data-testid={`academy-plan-invoice-download-${p.invoice_no}`}
                  >
                    <DownloadSimple size={13} /> INVOICE
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
